import { useState, useEffect, useRef } from 'react'
import { meshGateway } from './utils/meshGateway'
import './universal.css'

// Meshtastic Bluetooth service UUID (standard UUID for Meshtastic devices)
const MESHTASTIC_SERVICE_UUID = '6ba1b218-15a8-461f-9fa8-5dcae273eafd'

// Check if running on localhost for dev features
const isLocalhost = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.startsWith('192.168.')
)

/**
 * Meshtastic Chat Component
 * Connects to Meshtastic devices via Web Serial API or Web Bluetooth
 * Supports sending/receiving messages over the mesh network
 * Includes gateway support for relaying API requests for offline users
 */
function MeshtasticChat() {
  const [isConnected, setIsConnected] = useState(false)
  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState('')
  const [deviceInfo, setDeviceInfo] = useState(null)
  const [connectionType, setConnectionType] = useState('serial') // 'serial' or 'bluetooth'
  const [logs, setLogs] = useState([])
  const [isGatewayEnabled, setIsGatewayEnabled] = useState(false)
  const [availableGateways, setAvailableGateways] = useState(0)
  
  const portRef = useRef(null)
  const readerRef = useRef(null)
  const writerRef = useRef(null)
  const meshConnectionRef = useRef(null)

  // Check if Web Serial API is supported
  const isSerialSupported = 'serial' in navigator
  const isBluetoothSupported = 'bluetooth' in navigator

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString()
    setLogs(prev => [...prev.slice(-50), { timestamp, message, type }])
    if (isLocalhost) console.log(`[${type.toUpperCase()}] ${message}`)
  }

  // Connect via Web Serial API (USB)
  const connectSerial = async () => {
    try {
      addLog('Requesting serial port...')
      
      // Request a port from the user
      const port = await navigator.serial.requestPort({
        filters: [
          { usbVendorId: 0x10C4, usbProductId: 0xEA60 }, // CP2102 USB to UART
          { usbVendorId: 0x1A86, usbProductId: 0x7523 }, // CH340 USB to UART
          { usbVendorId: 0x239A }, // Adafruit boards
        ]
      })
      
      addLog('Opening serial port...')
      await port.open({ baudRate: 115200 })
      
      portRef.current = port
      setIsConnected(true)
      addLog('Connected via Serial!', 'success')
      
      // Create mesh connection wrapper
      const meshConnection = {
        send: async (data) => {
          try {
            const writer = port.writable.getWriter()
            const encoder = new TextEncoder()
            await writer.write(encoder.encode(data + '\n'))
            writer.releaseLock()
          } catch (error) {
            console.error('Mesh send error:', error)
          }
        },
        on: (event, callback) => {
          // Simple event emitter (you can enhance this)
          meshConnection._callbacks = meshConnection._callbacks || {}
          meshConnection._callbacks[event] = meshConnection._callbacks[event] || []
          meshConnection._callbacks[event].push(callback)
        },
        emit: (event, data) => {
          const callbacks = meshConnection._callbacks?.[event] || []
          callbacks.forEach(cb => cb(data))
        }
      }
      
      meshConnectionRef.current = meshConnection
      
      // Initialize mesh gateway
      await meshGateway.initialize(meshConnection)
      addLog('Mesh gateway initialized', 'success')
      
      // Start reading from the port
      startReading(port, meshConnection)
      
    } catch (error) {
      addLog(`Serial connection error: ${error.message}`, 'error')
      console.error('Serial connection error:', error)
    }
  }

  // Connect via Web Bluetooth
  const connectBluetooth = async () => {
    try {
      addLog('Requesting Bluetooth device...')
      
      const device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: 'Meshtastic' },
          { services: [MESHTASTIC_SERVICE_UUID] }
        ],
        optionalServices: [MESHTASTIC_SERVICE_UUID]
      })
      
      addLog('Connecting to GATT server...')
      const server = await device.gatt.connect()
      
      addLog('Getting Meshtastic service...')
      const service = await server.getPrimaryService(MESHTASTIC_SERVICE_UUID)
      
      setIsConnected(true)
      addLog('Connected via Bluetooth!', 'success')
      
      // TODO: Implement Bluetooth characteristic reading/writing
      
    } catch (error) {
      addLog(`Bluetooth connection error: ${error.message}`, 'error')
      console.error('Bluetooth connection error:', error)
    }
  }

  // Start reading from serial port
  const startReading = async (port, meshConnection) => {
    try {
      const reader = port.readable.getReader()
      readerRef.current = reader
      
      while (true) {
        const { value, done } = await reader.read()
        if (done) {
          addLog('Serial port closed', 'warning')
          break
        }
        
        // Process received data
        if (value) {
          const text = new TextDecoder().decode(value)
          addLog(`Received: ${text}`)
          
          // Try to parse as gateway message
          try {
            const parsed = JSON.parse(text)
            if (parsed.type && parsed.type.includes('gateway')) {
              // This is a gateway protocol message
              meshConnection?.emit('message', { type: parsed.type, data: parsed })
              addLog(`Gateway message: ${parsed.type}`, 'info')
            } else {
              // Regular chat message
              setMessages(prev => [...prev, {
                id: Date.now(),
                from: 'Device',
                text: text,
                timestamp: new Date(),
                direction: 'incoming'
              }])
            }
          } catch {
            // Not JSON, treat as regular message
            setMessages(prev => [...prev, {
              id: Date.now(),
              from: 'Device',
              text: text,
              timestamp: new Date(),
              direction: 'incoming'
            }])
          }
        }
      }
    } catch (error) {
      addLog(`Reading error: ${error.message}`, 'error')
      console.error('Reading error:', error)
    }
  }
  
  // Toggle gateway mode
  const toggleGateway = () => {
    if (isGatewayEnabled) {
      meshGateway.disableGateway()
      setIsGatewayEnabled(false)
      addLog('Gateway disabled', 'warning')
    } else {
      meshGateway.enableGateway()
      setIsGatewayEnabled(true)
      addLog('Gateway enabled - relaying requests', 'success')
    }
  }
  
  // Update gateway count periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setAvailableGateways(meshGateway.availableGateways.size)
    }, 2000)
    
    return () => clearInterval(interval)
  }, [])

  // Send message over mesh
  const sendMessage = async () => {
    if (!inputMessage.trim() || !isConnected) return
    
    try {
      if (connectionType === 'serial' && portRef.current) {
        const writer = portRef.current.writable.getWriter()
        const encoder = new TextEncoder()
        
        // In production, you'd encode this as a proper Meshtastic protobuf message
        const data = encoder.encode(inputMessage + '\n')
        await writer.write(data)
        writer.releaseLock()
        
        addLog(`Sent: ${inputMessage}`, 'success')
        
        setMessages(prev => [...prev, {
          id: Date.now(),
          from: 'You',
          text: inputMessage,
          timestamp: new Date(),
          direction: 'outgoing'
        }])
        
        setInputMessage('')
      }
    } catch (error) {
      addLog(`Send error: ${error.message}`, 'error')
      console.error('Send error:', error)
    }
  }

  // Disconnect
  const disconnect = async () => {
    try {
      if (readerRef.current) {
        await readerRef.current.cancel()
        readerRef.current = null
      }
      
      if (portRef.current) {
        await portRef.current.close()
        portRef.current = null
      }
      
      setIsConnected(false)
      addLog('Disconnected', 'warning')
    } catch (error) {
      addLog(`Disconnect error: ${error.message}`, 'error')
      console.error('Disconnect error:', error)
    }
  }

  return (
    <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: '1.8rem', fontWeight: '400' }}>
          🌐 Mesh Network
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Connect to Meshtastic devices for offline communication and API relay.
        </p>
      </div>
      
      {/* Connection Status Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ color: 'var(--text-primary)', margin: 0 }}>Connection Status</h3>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '0.4rem 0.8rem',
            borderRadius: '20px',
            backgroundColor: isConnected ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${isConnected ? 'var(--success)' : 'var(--error)'}`
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: isConnected ? 'var(--success)' : 'var(--error)'
            }}></div>
            <span style={{ 
              color: isConnected ? 'var(--success)' : 'var(--error)',
              fontSize: '0.85rem',
              fontWeight: '500'
            }}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
        
        {deviceInfo && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Device: {deviceInfo.name}
          </p>
        )}
        
        {isConnected && (
          <div style={{ 
            padding: '1rem',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>Gateway Mode</span>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                cursor: 'pointer'
              }}>
                <input 
                  type="checkbox" 
                  checked={isGatewayEnabled}
                  onChange={toggleGateway}
                  style={{ 
                    width: '18px', 
                    height: '18px',
                    accentColor: 'var(--accent)'
                  }}
                />
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  {isGatewayEnabled ? 'Relaying requests' : 'Disabled'}
                </span>
              </label>
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              color: 'var(--text-secondary)',
              fontSize: '0.85rem'
            }}>
              <span>📡 Available gateways: {availableGateways}</span>
              {isGatewayEnabled && (
                <span style={{ color: 'var(--success)' }}>
                  (Helping offline users)
                </span>
              )}
            </div>
            
            {!navigator.onLine && availableGateways > 0 && (
              <div style={{ 
                marginTop: '0.75rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: 'rgba(251, 191, 36, 0.15)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '6px',
                fontSize: '0.85rem',
                color: 'var(--text-primary)'
              }}>
                ℹ️ You're offline but can use {availableGateways} gateway(s) to access the internet
              </div>
            )}
          </div>
        )}

        {/* Connection Buttons */}
        {!isConnected ? (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={connectSerial}
              disabled={!isSerialSupported}
              className="btn btn-primary"
              style={{ 
                opacity: isSerialSupported ? 1 : 0.5,
                cursor: isSerialSupported ? 'pointer' : 'not-allowed'
              }}
            >
              🔌 Connect via USB
            </button>
            
            <button
              onClick={connectBluetooth}
              disabled={!isBluetoothSupported}
              className="btn btn-secondary"
              style={{ 
                opacity: isBluetoothSupported ? 1 : 0.5,
                cursor: isBluetoothSupported ? 'pointer' : 'not-allowed'
              }}
            >
              📡 Connect via Bluetooth
            </button>
          </div>
        ) : (
          <button
            onClick={disconnect}
            className="btn"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid var(--error)',
              color: 'var(--error)'
            }}
          >
            Disconnect
          </button>
        )}
      </div>

      {/* Browser Support Warning */}
      {!isSerialSupported && !isBluetoothSupported && (
        <div className="card" style={{ 
          marginBottom: '1.5rem',
          backgroundColor: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)'
        }}>
          <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
            ⚠️ Browser Not Supported
          </h4>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Your browser doesn't support Web Serial API or Web Bluetooth. 
            Please use Chrome, Edge, or Opera (version 89+).
          </p>
        </div>
      )}

      {/* Messages Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>📬 Messages</h3>
        
        <div style={{
          minHeight: '200px',
          maxHeight: '350px',
          overflowY: 'auto',
          padding: '1rem',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          {messages.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', textAlign: 'center', margin: '2rem 0' }}>
              No messages yet. Connect to start chatting!
            </p>
          ) : (
            messages.map(msg => (
              <div
                key={msg.id}
                style={{
                  padding: '0.75rem',
                  marginBottom: '0.75rem',
                  backgroundColor: msg.direction === 'outgoing' ? 'rgba(108, 143, 255, 0.1)' : 'var(--bg-tertiary)',
                  borderRadius: '8px',
                  border: `1px solid ${msg.direction === 'outgoing' ? 'rgba(108, 143, 255, 0.3)' : 'var(--border)'}`,
                  textAlign: msg.direction === 'outgoing' ? 'right' : 'left'
                }}
              >
                <div style={{ fontWeight: '500', color: 'var(--accent)', fontSize: '0.85rem' }}>
                  {msg.from}
                </div>
                <div style={{ color: 'var(--text-primary)', margin: '0.25rem 0' }}>
                  {msg.text}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  {msg.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Message Input */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Type a message..."
            disabled={!isConnected}
            className="form-control"
            style={{ flex: 1 }}
          />
          <button
            onClick={sendMessage}
            disabled={!isConnected || !inputMessage.trim()}
            className="btn btn-primary"
            style={{ 
              opacity: isConnected && inputMessage.trim() ? 1 : 0.5,
              cursor: isConnected && inputMessage.trim() ? 'pointer' : 'not-allowed'
            }}
          >
            Send
          </button>
        </div>
      </div>

      {/* Debug Logs - Only show on localhost */}
      {isLocalhost && (
        <details className="card" style={{ marginTop: '1rem' }}>
          <summary style={{ 
            cursor: 'pointer', 
            fontWeight: '500',
            color: 'var(--text-primary)',
            padding: '0.5rem 0'
          }}>
            🔍 Debug Logs ({logs.length})
          </summary>
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: '6px',
            maxHeight: '200px',
            overflowY: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.8rem'
          }}>
            {logs.map((log, i) => (
              <div key={i} style={{ 
                color: log.type === 'error' ? 'var(--error)' : 
                       log.type === 'success' ? 'var(--success)' : 
                       log.type === 'warning' ? '#fbbf24' : 'var(--text-secondary)',
                marginBottom: '0.25rem'
              }}>
                [{log.timestamp}] {log.message}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}

export default MeshtasticChat
